
import React, { useState } from 'react';
import AdminHeader from '@/components/admin/AdminHeader';
import SubmissionsManager from '@/components/admin/SubmissionsManager';
import AdminTerraceManager from '@/components/admin/AdminTerraceManager';
import ApiKeysManager from '@/components/admin/ApiKeysManager';
import AdminAuthGuard from '@/components/admin/AdminAuthGuard';
import SunStatusManager from '@/components/admin/SunStatusManager';
import { useAuth } from '@/hooks/useAuth';
import { useScreenSize } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import { 
  FileText, 
  MapPin, 
  Sun, 
  Key,
  ChevronDown
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('submissions');
  const { logout } = useAuth();
  const { isMobile } = useScreenSize();

  const handleLogout = async () => {
    await logout();
  };

  const tabs = [
    {
      id: 'submissions',
      label: isMobile ? 'Submissions' : 'Submissions',
      icon: FileText,
      component: SubmissionsManager
    },
    {
      id: 'terraces',
      label: isMobile ? 'Terraces' : 'Terraces',
      icon: MapPin,
      component: AdminTerraceManager
    },
    {
      id: 'sun-status',
      label: isMobile ? 'Sun' : 'Sun Status',
      icon: Sun,
      component: SunStatusManager
    },
    {
      id: 'api-keys',
      label: isMobile ? 'API' : 'API Keys',
      icon: Key,
      component: ApiKeysManager
    }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);
  const ActiveComponent = activeTabData?.component || SubmissionsManager;

  const renderMobileNavigation = () => (
    <div className="bg-white border-b border-border shadow-soft px-4 py-3">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="w-full justify-between h-12 border-border bg-white hover:bg-peach-orange/5 hover:shadow-soft transition-all duration-300"
          >
            <div className="flex items-center gap-3">
              {activeTabData?.icon && <activeTabData.icon className="h-5 w-5 text-peach-orange" />}
              <span className="font-medium text-sunset-dark">{activeTabData?.label}</span>
            </div>
            <ChevronDown className="h-4 w-4 text-sunset-medium" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          className="w-[calc(100vw-2rem)] max-w-none bg-white border-border shadow-warm"
          align="start"
        >
          {tabs.map((tab) => (
            <DropdownMenuItem
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-3 p-4 cursor-pointer text-base transition-all duration-200
                ${activeTab === tab.id 
                  ? 'bg-peach-orange/10 text-sunset-dark font-medium shadow-soft' 
                  : 'text-sunset-medium hover:bg-peach-orange/5 hover:text-sunset-dark'
                }
              `}
            >
              <tab.icon className="h-5 w-5" />
              <span>{tab.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const renderDesktopNavigation = () => (
    <div className="bg-gradient-to-r from-warm-cream to-white border-b border-border shadow-soft px-6 py-4">
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-warm">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? "default" : "ghost"}
            onClick={() => setActiveTab(tab.id)}
            className={`
              flex items-center gap-3 whitespace-nowrap transition-all duration-300 hover-lift
              ${activeTab === tab.id 
                ? 'bg-peach-orange hover:bg-peach-orange/90 text-white shadow-warm font-semibold border-0' 
                : 'text-sunset-medium hover:bg-peach-orange/10 hover:text-sunset-dark hover:shadow-soft border-transparent'
              }
            `}
          >
            <tab.icon className="h-4 w-4" />
            <span className="font-medium">{tab.label}</span>
          </Button>
        ))}
      </div>
    </div>
  );

  return (
    <AdminAuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-warm-cream via-white to-warm-cream/30">
        <AdminHeader onLogout={handleLogout} />
        
        {/* Enhanced Navigation */}
        {isMobile ? renderMobileNavigation() : renderDesktopNavigation()}
        
        {/* Content Area with better spacing and cards */}
        <div className={`
          ${isMobile ? 'px-3 py-4' : 'container mx-auto px-4 py-6'}
        `}>
          <div className="w-full animate-fade-in-up">
            <ActiveComponent />
          </div>
        </div>
      </div>
    </AdminAuthGuard>
  );
};

export default AdminDashboard;
